import React from 'react';
import config from '../../../../../config/config';
import FacebookV2API from '../../../../../api/FacebookV2API';
import Util from '../../../../../util/Util';

const FACEBOOK_REDIRECT_URI = window.location.origin + '/facebook-callback';
const CONFIGURATION_ID = '1111367754633118';

export const withFacebookAuth = (WrappedComponent) => {
  return class WithFacebookAuth extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        configLoaded: true,
        facebookAppId: null,
        connectionStatus: 'disconnected',
        connectedAccounts: [],
        selectedAccountId: null,
        accessToken: null,
        tokenExpiry: null,
        integrationId: null,
        connectionInfo: null,
        isAuthLoading: false,
        pages: [] // Managed globally by HOC
      };

      this.popupCheckInterval = null;
      this.processingAuthCode = false;
    }

    componentDidMount() {
      this.loadFacebookConfig();
      // this.loadAccounts();
      window.addEventListener('message', this.handleFacebookMessage);
    }

    componentWillUnmount() {
      window.removeEventListener('message', this.handleFacebookMessage);
      if (this.popupCheckInterval) {
        clearInterval(this.popupCheckInterval);
      }
    }

    loadFacebookConfig = () => {
      this.setState({ isAuthLoading: true });
      FacebookV2API.getConfig(
        (response) => {
          if (response && response.appId) {
            this.setState({ 
              facebookAppId: response.appId,
              configLoaded: true,
            }, () => {
              // PERSIST CONNECTION ON REFRESH: 
              // Once config is ready, immediately fetch existing active integrations
              this.loadAccounts();
            });
          } else {
            this.setState({ configLoaded: true, isAuthLoading: false });
          }
        },
        (error) => {
          this.setState({ configLoaded: false, isAuthLoading: false });
          Util.showError("Failed to load Facebook configuration");
        }
      );
    };

    loadPagesForAccount = (accountId) => {
      if (!accountId) return;
      
      this.setState({ isAuthLoading: true });
      FacebookV2API.fetchPages(
        accountId,
        ({ pages }) => {
          const pagesWithTokens = pages.map(page => ({
            ...page,
            has_token: !!page.access_token
          }));
          this.setState({ pages: pagesWithTokens, isAuthLoading: false });
        },
        (err) => {
          Util.showError("Failed to load pages: " + err);
          this.setState({ isAuthLoading: false });
        }
      );
    };

    connectFacebook = async (event) => {
      if (event) event.preventDefault();
          
      if (!this.state.configLoaded || !this.state.facebookAppId) {
        Util.showError("Facebook configuration not loaded. Please refresh the page.");
        return;
      }
  
      try {
        const configurationId = CONFIGURATION_ID;
        const loginUrl = `https://www.facebook.com/v25.0/dialog/oauth?client_id=${this.state.facebookAppId}` +
                          `&redirect_uri=${encodeURIComponent(FACEBOOK_REDIRECT_URI)}` +
                          `&config_id=${configurationId}&response_type=code&display=popup`;
        const popup = window.open(loginUrl, 'Facebook Login', 'width=600,height=400,left=100,top=100');
        
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          Util.showError("Popup was blocked. Please allow popups for this site.");
          return;
        }
        
        Util.showNotification("Info", "Facebook login window opened. Please complete the login.", "info");

        if (this.popupCheckInterval) clearInterval(this.popupCheckInterval);
        this.popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(this.popupCheckInterval);
            this.popupCheckInterval = null;
          }
        }, 1000);
        
      } catch (error) {
        Util.showError("Failed to initialize Facebook login");
      }
    }

    loadAccounts = (done) => {
      this.setState({ isAuthLoading: true });
      FacebookV2API.listAccounts(
        (response) => {
          const accounts = response.accounts || [];
          const primary = response.primary_account || accounts[0] || null;
          const previousAccountId = this.state.selectedAccountId;

          // Determine connection status based on DB response
          const connectionStatus = primary
            ? (primary.status === 'active' ? 'connected' : 'expired')
            : 'disconnected';

          // Retain previously selected account if it still exists, otherwise default to primary
          const selectedAccountId = previousAccountId && accounts.some(a => a.integration_id === previousAccountId)
            ? previousAccountId
            : (primary?.integration_id || accounts[0]?.integration_id || null);

          this.setState({
            connectedAccounts: accounts,
            connectionStatus,
            selectedAccountId,
            connectionInfo: accounts.find(a => a.integration_id === selectedAccountId) || primary,
            accessToken: primary?.access_token || null,
            tokenExpiry: primary?.expires_at || null,
            integrationId: primary?.integration_id || null,
            isAuthLoading: false
          }, () => {
            if (this.state.selectedAccountId) {
              this.loadPagesForAccount(this.state.selectedAccountId);
            }
            if (done) done();
          });
        },
        (error) => {
          console.error("Load accounts error:", error);
          this.setState({ isAuthLoading: false });
          if (done) done();
        }
      );
    };

    switchAccount = (accountId) => {
      const account = this.state.connectedAccounts.find(a => a.integration_id === accountId);
      if (account) {
        this.setState({ 
          selectedAccountId: accountId,
          connectionInfo: account,
          accessToken: account.access_token,
          tokenExpiry: account.expires_at,
          integrationId: account.integration_id,
        }, () => {
          this.loadPagesForAccount(accountId);
        });
      }
    };

    disconnectAccount = (accountId, accountName) => {
      if (window.confirm(`Are you sure you want to disconnect ${accountName}? All integrations for this account will stop working.`)) {
        this.setState({ isAuthLoading: true });
        FacebookV2API.disconnect(
          accountId,
          (response) => {
            Util.showNotification("Success", `${accountName} disconnected successfully`, "success");
            this.loadAccounts(); // Re-fetches list, automatically sets status to 'disconnected' if empty
          },
          (err) => {
            Util.showError("Failed to disconnect: " + err);
            this.setState({ isAuthLoading: false });
          }
        );
      }
    };

    disconnectFacebook = () => {
      if (window.confirm('Are you sure you want to disconnect all Facebook accounts?')) {
        this.setState({ isAuthLoading: true });
        FacebookV2API.disconnect(
          'all',
          (response) => {
            Util.showNotification("Success", "All Facebook accounts disconnected", "success");
            this.loadAccounts();
          },
          (err) => {
            Util.showError("Failed to disconnect: " + err);
            this.setState({ isAuthLoading: false });
          }
        );
      }
    };

    refreshToken = () => {
      this.setState({ isAuthLoading: true });
      FacebookV2API.refreshToken(
        (response) => {
          Util.showNotification("Success", "Token refreshed successfully", "success");
          this.loadAccounts();
        },
        (err) => {
          Util.showError("Failed to refresh token: " + err);
          this.setState({ isAuthLoading: false });
        }
      );
    };

    handleFacebookMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'facebook_auth') return;

      if (event.data.error) {
        Util.showError("Facebook login failed: " + (event.data.errorDescription || event.data.error));
        return;
      }
      if (!event.data.code) return;
      if (this.processingAuthCode) return;   // single-use code: never exchange twice
      this.processingAuthCode = true;

      this.setState({ isAuthLoading: true });
      FacebookV2API.storeToken(
        { code: event.data.code, redirectUri: FACEBOOK_REDIRECT_URI },
        (response) => {
          this.processingAuthCode = false;
          Util.showNotification(
            "Success",
            response?.reconnected ? "Facebook reconnected — token refreshed" : "Facebook connected successfully", 
            "success"
          );
          
          this.loadAccounts(() => {
            const first = this.state.connectedAccounts[0];
            if (first && !this.state.selectedAccountId) {
               this.switchAccount(first.integration_id);
            }
          });
        },
        (error) => {
          this.processingAuthCode = false;
          let msg = error;
          try { msg = JSON.parse(`${error}`)?.message ?? error; } catch (e) {}
          Util.showError("Failed to connect: " + msg);
          this.setState({ isAuthLoading: false });
        }
      );
    }

    render() {
      const fbAuth = {
        ...this.state,
        connectFacebook: this.connectFacebook,
        switchAccount: this.switchAccount,
        disconnectAccount: this.disconnectAccount,
        disconnectFacebook: this.disconnectFacebook,
        refreshToken: this.refreshToken,
        loadAccounts: this.loadAccounts
      };

      return <WrappedComponent fbAuth={fbAuth} {...this.props} />;
    }
  };
};